import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ShoppingBag, MessageSquare, ArrowLeft, CheckCircle2, Minus, ArrowRight, Sparkles, FileText, Package, Check } from 'lucide-react';
import SEO from '../components/SEO';

export default function Products() {
  const { category } = useParams();
  const [products, setProducts] = useState([]);
  const [categoryName, setCategoryName] = useState('');
  const [loading, setLoading] = useState(true);
  const [formStatus, setFormStatus] = useState('idle'); // idle, submitting, success, error
  const [selectedOption, setSelectedOption] = useState('begge'); // provepakke, bestillingsseddel, begge

  // Attached images (STRICTLY ONLY THESE, NO AI IMAGES)
  const attachedImages = [
    {
      url: 'https://assets.auxodev.dk/assets/19777474-0f63-4a2a-88a2-aa8209d1f0a9/74652349-2d2d-4ea5-b511-3d5aefcfd25f.jpg',
      title: 'Blå Hortensia',
      description: 'Et smukt og detaljeret motiv af en blå hortensia, trykt på vores miljøvenlige, matte papir.'
    },
    {
      url: 'https://assets.auxodev.dk/assets/19777474-0f63-4a2a-88a2-aa8209d1f0a9/dd4424ac-8ab6-46e1-9378-f371addf8535.png',
      title: 'Blå med humle (Kort nr. 111)',
      description: 'Håndtegnede botaniske motiver med humle, der bringer naturens ro helt tæt på.'
    },
    {
      url: 'https://assets.auxodev.dk/assets/19777474-0f63-4a2a-88a2-aa8209d1f0a9/3afc337c-d316-426c-8808-1a0aa0c15677.png',
      title: 'Tusindfryd (Kort nr. 112)',
      description: 'En fin og elegant illustration af den klassiske tusindfryd, der udstråler lethed og glæde.'
    },
    {
      url: 'https://assets.auxodev.dk/assets/19777474-0f63-4a2a-88a2-aa8209d1f0a9/a5693a1a-2c68-4848-a3ef-ffde2536f504.jpg',
      title: 'Vild med vilje',
      description: 'Inspireret af den vilde, danske natur og de smukke, utæmmede blomsterenge.'
    },
    {
      url: 'https://assets.auxodev.dk/assets/19777474-0f63-4a2a-88a2-aa8209d1f0a9/6db6425c-0249-48d3-bfaa-76b4e20eb057.jpg',
      title: 'Valmuer orange (Kort nr. 103)',
      description: 'Varme, orange valmuer, der tilføjer et strejf af farve og personlighed til din hilsen.'
    },
    {
      url: 'https://assets.auxodev.dk/assets/19777474-0f63-4a2a-88a2-aa8209d1f0a9/34be05c3-08ef-4d94-9398-2f523a480ebd.png',
      title: 'Tusindfryd m. kløver (Kort nr. 113)',
      description: 'En harmonisk sammensætning af tusindfryd og fine kløverblade.'
    },
    {
      url: 'https://assets.auxodev.dk/assets/19777474-0f63-4a2a-88a2-aa8209d1f0a9/0175b911-fa37-4d44-933a-862fe038eb90.jpg',
      title: 'Blå vilde',
      description: 'Delikate blå nuancer og fine linjer, der fanger øjet og skaber en følelse af ro.'
    }
  ];

  useEffect(() => {
    setLoading(true);
    
    Promise.all([
      fetch('https://api.dev.auxo.dk/public/content/products').then(res => res.ok ? res.json() : []),
      fetch('https://api.dev.auxo.dk/public/content/product_categories').then(res => res.ok ? res.json() : [])
    ])
    .then(([productsResponse, categoriesResponse]) => {
      const productsData = Array.isArray(productsResponse) ? productsResponse : (productsResponse.data || []);
      const categoriesData = Array.isArray(categoriesResponse) ? categoriesResponse : (categoriesResponse.data || []);

      const currentCategory = category?.toLowerCase().trim();

      // Block private categories
      if (currentCategory && currentCategory.includes('private')) {
        setCategoryName('Ikke tilgængelig');
        setProducts([]);
        setLoading(false);
        return;
      }

      // Handle category name
      if (currentCategory === 'alle' || currentCategory === 'blomsterkort') {
        setCategoryName('Alle produkter');
      } else {
        const cat = categoriesData.find(c => c.category_id?.toLowerCase().trim() === currentCategory);
        if (cat) {
          setCategoryName(cat.name.da);
        } else {
          setCategoryName('Kollektion');
        }
      }

      // Handle products
      const filtered = (currentCategory === 'alle' || currentCategory === 'blomsterkort') 
        ? productsData 
        : productsData.filter(p => p.category_id?.toLowerCase().trim() === currentCategory);
      
      // Sort products by title
      const sorted = [...filtered].sort((a, b) => {
        const titleA = a.title?.da || '';
        const titleB = b.title?.da || '';
        return titleA.localeCompare(titleB, 'da', { numeric: true, sensitivity: 'base' });
      });
      
      setProducts(sorted);
      setLoading(false);
    })
    .catch(err => {
      console.error('Error fetching data:', err);
      setLoading(false);
    });
  }, [category]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setFormStatus('submitting');
    
    const formData = new FormData(e.target);
    const data = Object.fromEntries(formData.entries());
    
    // Add selected option explicitly to data
    data.bestillingstype = selectedOption === 'provepakke' 
      ? 'Prøvepakke' 
      : selectedOption === 'bestillingsseddel' 
      ? 'Bestillingsseddel' 
      : 'Både Prøvepakke & Bestillingsseddel';

    try {
      const response = await fetch('https://api.dev.auxo.dk/webform/submit', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(data),
      });
      
      if (response.ok) {
        setFormStatus('success');
        e.target.reset();
      } else {
        setFormStatus('error');
      }
    } catch (error) {
      setFormStatus('error');
    }
  };

  if (loading) {
    return (
      <div className="min-h-[60vh] flex items-center justify-center bg-white">
        <div className="w-12 h-12 border-t-2 border-brand-950 rounded-full animate-spin"></div>
      </div>
    );
  }

  // CUSTOM SHOWCASE FOR "HELE KOLLEKTIONEN" (category === 'alle' or 'blomsterkort')
  if (category?.toLowerCase().trim() === 'alle' || category?.toLowerCase().trim() === 'blomsterkort') {
    const productsSchema = {
      "@context": "https://schema.org",
      "@type": "CollectionPage",
      "name": `${categoryName || 'Blomsterkort'} - Cards by Eve`,
      "description": `Udforsk det fulde udvalg af håndtegnede blomsterkort og lykønskningskort fra Cards by Eve. Trykt på miljøvenligt, mat kvalitetspapir.`,
      "numberOfItems": products.length,
      "itemListElement": products.map((product, index) => ({
        "@type": "ListItem",
        "position": index + 1,
        "name": product.title?.da || '',
        "image": product.images?.[0] || ''
      }))
    };

    return (
      <div className="bg-white">
        <SEO 
          title={categoryName || 'Blomsterkort'}
          description={`Udforsk vores kollektion af ${categoryName ? categoryName.toLowerCase() : 'blomsterkort'} hos Cards by Eve. Håndtegnede motiver trykt på miljøvenligt, mat kvalitetspapir.`}
          schema={productsSchema}
        />
        {/* Hero Section */}
        <header className="pt-24 pb-20 bg-brand-50 relative overflow-hidden">
          {/* Decorative background elements */}
          <div className="absolute right-0 top-0 w-1/3 h-full opacity-10 pointer-events-none">
            <img 
              src={attachedImages[0].url} 
              alt="" 
              className="w-full h-full object-cover filter grayscale"
              loading="lazy"
              decoding="async"
            />
          </div>
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
            <div className="flex items-center space-x-4 mb-8">
              <Minus className="w-6 h-6 text-brand-950" />
              <span className="text-brand-400 text-xs uppercase tracking-[0.3em] font-medium">
                En verden af muligheder
              </span>
            </div>
            <h1 className="text-5xl md:text-7xl lg:text-8xl font-serif text-brand-950 leading-tight mb-8">
              Blomsterkort
            </h1>
            <p className="text-brand-800 text-lg md:text-xl font-light max-w-3xl leading-relaxed mb-10">
              Udforsk mine håndtegnede blomsterkort, trykt på en pæn mat og miljøvenlig kvalitet. Mine motiver er inspireret af den vilde natur, mine egne favoritblomster og den fri fantasi – skabt med kærlighed til detaljen og det gode håndværk.
            </p>
            <div className="flex flex-wrap gap-4">
              <a 
                href="#bestil" 
                className="inline-flex items-center justify-center bg-brand-950 text-white px-8 py-4 text-xs uppercase tracking-widest font-medium hover:bg-brand-800 transition-all duration-500 shadow-lg shadow-brand-950/10"
              >
                Bestil prøvepakke / bestillingsseddel
                <ArrowRight className="ml-3 w-4 h-4" />
              </a>
            </div>
          </div>
        </header>

        {/* Showcase of Possibilities */}
        <section className="py-32 bg-white">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center max-w-3xl mx-auto mb-24">
              <span className="text-brand-400 text-xs uppercase tracking-[0.3em] font-medium block mb-6">
                Fremvisning
              </span>
              <h2 className="text-4xl md:text-5xl font-serif text-brand-950 mb-6">
                Kvalitet, der taler <br />
                <span className="italic">til sanserne</span>
              </h2>
              <p className="text-brand-600 font-light leading-relaxed">
                Mine kort er skabt med øje for den mindste detalje. Jeg kombinerer fine, håndtegnede streger med nøje udvalgt papir for at skabe et taktilt og luksuriøst udtryk. Gå på opdagelse i mulighederne herunder.
              </p>
            </div>

            {/* Editorial Asymmetric Grid */}
            <div className="space-y-32">
              {/* Shelf Feature Section */}
              <div className="grid grid-cols-1 lg:grid-cols-12 gap-16 items-center">
                <div className="lg:col-span-7">
                  <div className="aspect-[4/3] overflow-hidden rounded-sm shadow-xl bg-brand-50">
                    <img 
                      src="https://assets.auxodev.dk/assets/19777474-0f63-4a2a-88a2-aa8209d1f0a9/3097ed61-1367-4683-a2b2-c84a1bc49131.png" 
                      alt="Håndlavet kortreol i resttræ til elegant præsentation af blomsterkort i butikker"
                      className="w-full h-full object-cover hover:scale-105 transition-transform duration-1000"
                      loading="lazy"
                      decoding="async"
                    />
                  </div>
                </div>
                <div className="lg:col-span-5 space-y-6">
                  <span className="text-brand-400 text-xs uppercase tracking-[0.2em] font-medium">Præsentation</span>
                  <h3 className="text-3xl md:text-4xl font-serif text-brand-950">Smuk præsentation i butikken</h3>
                  <p className="text-brand-600 font-light leading-relaxed">
                    Mine kort kan præsenteres på de fine, håndlavede reoler i resttræ, som skaber en hyggelig og naturlig stemning. Reolerne er tilpasset til at fremvise kortene på den mest indbydende måde.
                  </p>
                  <div className="h-px w-16 bg-brand-200 pt-2"></div>
                </div>
              </div>

              {/* Inspiration Gallery Grid */}
              <div>
                <div className="text-center max-w-3xl mx-auto mb-16">
                  <span className="text-brand-400 text-xs uppercase tracking-[0.3em] font-medium block mb-4">
                    Inspiration
                  </span>
                  <h3 className="text-3xl font-serif text-brand-950 mb-4">
                    Et udpluk af mine motiver
                  </h3>
                </div>

                <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-8">
                  {attachedImages.map((img, idx) => (
                    <div key={idx} className="group relative aspect-[3/4] overflow-hidden rounded-sm shadow-md bg-brand-50">
                      <img 
                        src={img.url} 
                        alt={`Inspiration: ${img.title} - Håndtegnet blomsterkort fra Cards by Eve`}
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-1000"
                        loading="lazy"
                        decoding="async"
                      />
                      <div className="absolute inset-0 bg-brand-950/0 group-hover:bg-brand-950/5 transition-colors duration-500"></div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Order Form Section */}
        <section id="bestil" className="py-32 bg-brand-50 relative overflow-hidden">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
            <div className="bg-white p-8 md:p-16 border border-brand-100 shadow-2xl max-w-4xl mx-auto">
              <div className="text-center mb-16">
                <span className="text-brand-400 text-xs uppercase tracking-[0.3em] font-medium block mb-6">
                  Forespørgsel
                </span>
                <h2 className="text-4xl md:text-5xl font-serif text-brand-950 mb-6 italic">
                  Oplev kvaliteten selv
                </h2>
                <p className="text-brand-600 font-light leading-relaxed max-w-xl mx-auto">
                  Udfyld formularen herunder for at modtage en prøvepakke med udvalgte kort eller min trykte bestillingsseddel med det fulde overblik.
                </p>
              </div>

              {/* Selection Tabs */}
              <div className="grid grid-cols-3 gap-4 mb-12">
                <button
                  type="button"
                  onClick={() => setSelectedOption('provepakke')}
                  className={`py-4 px-2 text-center border rounded-sm transition-all duration-300 flex flex-col items-center justify-center gap-2 cursor-pointer ${
                    selectedOption === 'provepakke'
                      ? 'border-brand-950 bg-brand-950 text-white'
                      : 'border-brand-200 hover:border-brand-400 text-brand-600'
                  }`}
                >
                  <Package className="w-5 h-5" />
                  <span className="text-[10px] uppercase tracking-widest font-bold">Prøvepakke</span>
                </button>
                <button
                  type="button"
                  onClick={() => setSelectedOption('bestillingsseddel')}
                  className={`py-4 px-2 text-center border rounded-sm transition-all duration-300 flex flex-col items-center justify-center gap-2 cursor-pointer ${
                    selectedOption === 'bestillingsseddel'
                      ? 'border-brand-950 bg-brand-950 text-white'
                      : 'border-brand-200 hover:border-brand-400 text-brand-600'
                  }`}
                >
                  <FileText className="w-5 h-5" />
                  <span className="text-[10px] uppercase tracking-widest font-bold">Bestillingsseddel</span>
                </button>
                <button
                  type="button"
                  onClick={() => setSelectedOption('begge')}
                  className={`py-4 px-2 text-center border rounded-sm transition-all duration-300 flex flex-col items-center justify-center gap-2 cursor-pointer ${
                    selectedOption === 'begge'
                      ? 'border-brand-950 bg-brand-950 text-white'
                      : 'border-brand-200 hover:border-brand-400 text-brand-600'
                  }`}
                >
                  <Sparkles className="w-5 h-5" />
                  <span className="text-[10px] uppercase tracking-widest font-bold">Begge dele</span>
                </button>
              </div>

              {formStatus === 'success' ? (
                <motion.div
                  initial={{ opacity: 0, scale: 0.98 }}
                  animate={{ opacity: 1, scale: 1 }}
                  className="text-center py-12"
                >
                  <CheckCircle2 className="w-16 h-16 text-brand-950 mx-auto mb-8" />
                  <h3 className="text-3xl font-serif text-brand-950 mb-4">Mange tak for din forespørgsel!</h3>
                  <p className="text-brand-600 font-light mb-10 max-w-md mx-auto">
                    Jeg har modtaget din forespørgsel om {
                      selectedOption === 'provepakke' ? 'en prøvepakke' : selectedOption === 'bestillingsseddel' ? 'en bestillingsseddel' : 'både prøvepakke og bestillingsseddel'
                    }. Jeg pakker og sender det afsted til dig hurtigst muligt.
                  </p>
                  <button
                    onClick={() => setFormStatus('idle')}
                    className="text-brand-950 text-xs uppercase tracking-widest font-bold border-b border-brand-950 pb-2 hover:text-brand-600 hover:border-brand-600 transition-colors"
                  >
                    Send en ny forespørgsel
                  </button>
                </motion.div>
              ) : (
                <form onSubmit={handleSubmit} className="space-y-8">
                  {/* Honeypot field (STRICTLY REQUIRED FOR ANTISPAM) */}
                  <div className="sr-only absolute" aria-hidden="true">
                    <input type="text" name="website_url" tabIndex="-1" autoComplete="off" />
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                    <div className="space-y-2">
                      <label htmlFor="shop_name" className="text-[10px] uppercase tracking-widest font-bold text-brand-400">Butiksnavn *</label>
                      <input
                        type="text"
                        id="shop_name"
                        name="shop_name"
                        required
                        className="w-full bg-transparent border-b border-brand-200 py-3 focus:border-brand-950 outline-none transition-colors font-light text-brand-950"
                        placeholder="Butikkens navn"
                      />
                    </div>
                    <div className="space-y-2">
                      <label htmlFor="contact_person" className="text-[10px] uppercase tracking-widest font-bold text-brand-400">Kontaktperson *</label>
                      <input
                        type="text"
                        id="contact_person"
                        name="contact_person"
                        required
                        className="w-full bg-transparent border-b border-brand-200 py-3 focus:border-brand-950 outline-none transition-colors font-light text-brand-950"
                        placeholder="Dit fulde navn"
                      />
                    </div>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                    <div className="space-y-2">
                      <label htmlFor="email" className="text-[10px] uppercase tracking-widest font-bold text-brand-400">E-mail *</label>
                      <input
                        type="email"
                        id="email"
                        name="email"
                        required
                        className="w-full bg-transparent border-b border-brand-200 py-3 focus:border-brand-950 outline-none transition-colors font-light text-brand-950"
                        placeholder="din@email.dk"
                      />
                    </div>
                    <div className="space-y-2">
                      <label htmlFor="phone" className="text-[10px] uppercase tracking-widest font-bold text-brand-400">Telefonnummer *</label>
                      <input
                        type="tel"
                        id="phone"
                        name="phone"
                        required
                        className="w-full bg-transparent border-b border-brand-200 py-3 focus:border-brand-950 outline-none transition-colors font-light text-brand-950"
                        placeholder="+45 12 34 56 78"
                      />
                    </div>
                  </div>

                  <div className="space-y-2">
                    <label htmlFor="message" className="text-[10px] uppercase tracking-widest font-bold text-brand-400">Eventuel besked eller særlige ønsker</label>
                    <textarea
                      id="message"
                      name="message"
                      rows="3"
                      className="w-full bg-transparent border-b border-brand-200 py-3 focus:border-brand-950 outline-none transition-colors font-light text-brand-950 resize-none"
                      placeholder="Skriv hvis du har specifikke ønsker til motiver eller formater..."
                    ></textarea>
                  </div>

                  {formStatus === 'error' && (
                    <p className="text-red-600 text-sm font-light">
                      Der skete en fejl under afsendelsen. Prøv venligst igen eller kontakt mig direkte på evelyn@cardsbyeve.dk.
                    </p>
                  )}

                  <button
                    type="submit"
                    disabled={formStatus === 'submitting'}
                    className="w-full bg-brand-950 text-white py-6 text-xs uppercase tracking-widest font-bold hover:bg-brand-800 transition-all disabled:opacity-50 flex items-center justify-center group cursor-pointer"
                  >
                    {formStatus === 'submitting' ? (
                      <span className="flex items-center">
                        <svg className="animate-spin -ml-1 mr-3 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                        </svg>
                        Sender forespørgsel...
                      </span>
                    ) : (
                      <span className="flex items-center">
                        Send forespørgsel <ArrowRight className="ml-2 w-4 h-4 group-hover:translate-x-1 transition-transform" />
                      </span>
                    )}
                  </button>
                </form>
              )}
            </div>
          </div>
        </section>
      </div>
    );
  }

  // EXISTING CATEGORY-SPECIFIC VIEW (for other categories like 'chokolade', 'blomsterkort-a6' etc.)
  const categorySchema = {
    "@context": "https://schema.org",
    "@type": "CollectionPage",
    "name": `${categoryName || 'Kollektion'} - Cards by Eve`,
    "description": `Udforsk vores ${categoryName ? categoryName.toLowerCase() : 'kollektion'} af håndtegnede produkter fra Cards by Eve.`,
    "numberOfItems": products.length,
    "itemListElement": products.map((product, index) => ({
      "@type": "ListItem",
      "position": index + 1,
      "name": product.title?.da || '',
      "image": product.images?.[0] || ''
    }))
  };

  return (
    <div className="bg-white">
      <SEO 
        title={categoryName || 'Kollektion'}
        description={`Se vores udvalg af ${categoryName ? categoryName.toLowerCase() : 'produkter'} hos Cards by Eve. Håndtegnede motiver trykt på miljøvenligt, mat kvalitetspapir.`}
        schema={categorySchema}
      />
      {/* Header */}
      <header className="pt-24 pb-20 bg-brand-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center space-x-4 mb-8">
            <Minus className="w-6 h-6 text-brand-950" />
            <span className="text-brand-400 text-xs uppercase tracking-[0.3em] font-medium">
              Kollektion
            </span>
          </div>
          <h1 className="text-6xl md:text-8xl font-serif text-brand-950 leading-tight mb-8">
            {categoryName.includes(' ') ? (
              <>
                {categoryName.split(' ')[0]} <br />
                <span className="italic">{categoryName.split(' ').slice(1).join(' ')}</span>
              </>
            ) : (
              categoryName
            )}
          </h1>
          <p className="text-brand-600 text-lg font-light max-w-2xl leading-relaxed">
            {categoryName.toLowerCase().includes('chokolade') 
              ? 'Lækker mørk chokolade med nødder fra Aalborg Chokolade, prydet med mit unikke print. Denne specialitet laves udelukkende på bestilling til virksomheder.'
              : categoryName === 'Forskellige reoler'
              ? 'Mine reoler er håndlavet i resttræ og tilpasset til at præsentere kortene på smukkeste vis. Fås i både sort og brændt træ.'
              : `Udforsk mine håndtegnede ${categoryName.toLowerCase()}, trykt på en pæn mat og miljøvenlig kvalitet. Skabt med kærlighed til detaljen og det gode håndværk.`}
          </p>
        </div>
      </header>

      {/* Product Grid */}
      <section className="py-32">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {products.length > 0 ? (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-16">
              {products.map((product, index) => (
                <motion.div
                  key={product.id}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: index * 0.05, duration: 0.8 }}
                  className="group"
                >
                  <div className="bg-white border border-brand-100 p-6 rounded-sm hover:shadow-xl transition-all duration-500 h-full flex flex-col">
                    <div className="aspect-[3/4] overflow-hidden rounded-sm bg-brand-50 mb-8 relative">
                      <img
                        src={product.images[0]}
                        alt={`Produkt: ${product.title.da} - Håndtegnet design fra Cards by Eve`}
                        className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-1000"
                        loading="lazy"
                        decoding="async"
                      />
                      <div className="absolute inset-0 bg-brand-950/0 group-hover:bg-brand-950/5 transition-colors duration-500"></div>
                    </div>
                    <h3 className="text-2xl font-serif text-brand-950 mb-3">{product.title.da}</h3>
                    <div 
                      className="text-brand-500 text-sm font-light leading-relaxed mb-8 line-clamp-3 flex-grow"
                      dangerouslySetInnerHTML={{ __html: product.description?.da || '' }}
                    />
                    <div className="h-px w-12 bg-brand-200 group-hover:w-full transition-all duration-700 mt-auto"></div>
                  </div>
                </motion.div>
              ))}
            </div>
          ) : (
            <div className="text-center py-32 border border-dashed border-brand-200">
              <p className="text-brand-400 font-light italic">Der er i øjeblikket ingen produkter i denne kategori.</p>
            </div>
          )}
        </div>
      </section>

      {/* Inquiry Form */}
      <section className="py-32 bg-brand-50 relative overflow-hidden">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="bg-white p-8 md:p-16 border border-brand-100 shadow-2xl max-w-4xl mx-auto">
            <div className="text-center mb-16">
              <span className="text-brand-400 text-xs uppercase tracking-[0.3em] font-medium block mb-6">Forespørgsel</span>
              <h2 className="text-4xl md:text-5xl font-serif text-brand-950 mb-6 italic">Interesseret i {categoryName}?</h2>
              <p className="text-brand-600 font-light leading-relaxed max-w-xl mx-auto">
                Udfyld formularen herunder, så kontakter jeg dig med mere information og priser til forhandlere.
              </p>
            </div>

            {formStatus === 'success' ? (
              <motion.div
                initial={{ opacity: 0, scale: 0.98 }}
                animate={{ opacity: 1, scale: 1 }}
                className="text-center py-12"
              >
                <CheckCircle2 className="w-16 h-16 text-brand-950 mx-auto mb-8" />
                <h3 className="text-3xl font-serif text-brand-950 mb-4">Tak for din besked</h3>
                <p className="text-brand-600 font-light mb-10">Jeg har modtaget din forespørgsel og vender tilbage hurtigst muligt.</p>
                <button
                  onClick={() => setFormStatus('idle')}
                  className="text-brand-950 text-xs uppercase tracking-widest font-bold border-b border-brand-950 pb-2"
                >
                  Send en ny besked
                </button>
              </motion.div>
            ) : (
              <form onSubmit={handleSubmit} className="space-y-10">
                {/* Honeypot */}
                <div className="sr-only absolute" aria-hidden="true">
                  <input type="text" name="website_url" tabIndex="-1" autoComplete="off" />
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-10">
                  <div className="space-y-2">
                    <label htmlFor="name" className="text-[10px] uppercase tracking-widest font-bold text-brand-400">Navn / Butik</label>
                    <input
                      type="text"
                      id="name"
                      name="name"
                      required
                      className="w-full bg-transparent border-b border-brand-200 py-3 focus:border-brand-950 outline-none transition-colors font-light text-brand-950"
                      placeholder="Dit navn eller butik"
                    />
                  </div>
                  <div className="space-y-2">
                    <label htmlFor="email" className="text-[10px] uppercase tracking-widest font-bold text-brand-400">E-mail</label>
                    <input
                      type="email"
                      id="email"
                      name="email"
                      required
                      className="w-full bg-transparent border-b border-brand-200 py-3 focus:border-brand-950 outline-none transition-colors font-light text-brand-950"
                      placeholder="din@email.dk"
                    />
                  </div>
                </div>

                <div className="space-y-2">
                  <label htmlFor="subject" className="text-[10px] uppercase tracking-widest font-bold text-brand-400">Emne</label>
                  <input
                    type="text"
                    id="subject"
                    name="subject"
                    defaultValue={`Forespørgsel på ${categoryName}`}
                    className="w-full bg-transparent border-b border-brand-200 py-3 focus:border-brand-950 outline-none transition-colors font-light text-brand-400"
                    readOnly
                  />
                </div>

                <div className="space-y-2">
                  <label htmlFor="message" className="text-[10px] uppercase tracking-widest font-bold text-brand-400">Besked</label>
                  <textarea
                    id="message"
                    name="message"
                    rows="4"
                    required
                    className="w-full bg-transparent border-b border-brand-200 py-3 focus:border-brand-950 outline-none transition-colors font-light text-brand-950 resize-none"
                    placeholder="Skriv din besked her..."
                  ></textarea>
                </div>

                <button
                  type="submit"
                  disabled={formStatus === 'submitting'}
                  className="w-full bg-brand-950 text-white py-6 text-xs uppercase tracking-widest font-bold hover:bg-brand-800 transition-all disabled:opacity-50 flex items-center justify-center group"
                >
                  {formStatus === 'submitting' ? (
                    <span className="flex items-center">
                      <svg className="animate-spin -ml-1 mr-3 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                      </svg>
                      Sender...
                    </span>
                  ) : (
                    <span className="flex items-center">
                      Send forespørgsel <ArrowRight className="ml-2 w-4 h-4 group-hover:translate-x-1 transition-transform" />
                    </span>
                  )}
                </button>
              </form>
            )}
          </div>
        </div>
      </section>
    </div>
  );
}
